import { Form, Row, Col, Dropdown } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  FormFieldProps,
  DropdownFieldProps,
  FormComponentProps,
} from "../interfaces/formInterfaceProps";
import './FormComponent.module.css';
import { validateCompanyName, validateFirstName, validateLastName, 
        validateDob, validateEmail,  validatePhoneNumber, 
        validateStreet, validateZip, validateCity } from '../validationFunctions/inputValidations.tsx';

import CONSTANTS from "../utils/constants";
import React, { useState } from "react";
import {
  validateAccountHolder,
} from "../validationFunctions/inputValidations.tsx";


const FormField: React.FC<FormFieldProps> = ({
  visible,
  controlId,
  label,
  value,
  onChange,
  validationFn,
  dispatch,
  placeholder,
  type = "text",
  disabled = false,
  dataTestId,
  errorStatus = false,
  userInputState,
  setUserInputState,
  inputKey
}) => {
  if (!visible) return null;

  return (
    <Form.Group controlId={controlId}>
      <Form.Label className="form-label" data-testid={dataTestId}>
        {label} <span className="required-field">*</span>
      </Form.Label>
      <Form.Control
        type={type}
        value={value}
        onChange={(e) => {onChange(e); validationFn(e.target.value, dispatch)}}
        onBlur={(e) => {validationFn(e.target.value, dispatch);setUserInputState({...userInputState, [inputKey]: ''})}}
        onFocus={() => setUserInputState({...userInputState, [inputKey]: 'typing'})}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={dataTestId.replace("-label", "-input")}
        className={errorStatus ? 'error' : ''}
      />
    </Form.Group>
  );
};

//custom error message component
const FormFieldError: React.FC<{
  id: string;
  value: string;
  userState: string;
  testId: string;
}> = ({ id, value, userState, testId }) => (
  userState !== 'typing' && value !== '' && 
  <span className="error_message" id={id} data-testid={testId}>
    <FormattedMessage id={value} />
  </span>
)

const DropdownField: React.FC<DropdownFieldProps> = ({
  disable,
  visible,
  controlId,
  label,
  selectedItem,
  handleSelect,
  options,
  dataTestId,
  required
}) => {
  if (!visible) return null;

  return (
    <Form.Group controlId={controlId}>
      <Form.Label data-testid={dataTestId}>
        {label} {required ? (<span style={{ color: '#F50057' }}>*</span>) : ""}
      </Form.Label>
      <Dropdown onSelect={handleSelect} className="w-100 volte-medium">
        <Dropdown.Toggle disabled={disable}
          className={`w-100 d-flex justify-content-between align-items-center ${disable ? "disabled-dropdown" : ""}`}
          id={controlId}
          style={{ backgroundColor: "white", borderColor: "#b1b6ba" }}
          data-testid={dataTestId.replace("-label", "-input")}
        >
          {selectedItem}
        </Dropdown.Toggle>
        <Dropdown.Menu className="w-100 rounded-3 form-dropdown">
          {options.map((option) => (
            <Dropdown.Item
              eventKey={option.value}
              key={option.value}
              data-testid={option.testId}
            >
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </Form.Group>
  );
};

const FormComponent: React.FC<FormComponentProps> = ({
  paymentMethodCode,
  fieldVisibility,
  onSelect,
  formData,
  onChange,
  handleDateChange,
  handleSelectCountry,
  formErrorState,
  dispatch, 
  userInputState,
  setUserInputState,
  isOverviewPage,
  onEditingBillingInfoChange
}) => {

  const [isEditingCustomerInfo, setIsEditingCustomerInfo] = useState(false);
  const [isEditingContactInfo, setIsEditingContactInfo] = useState(false);
  const [isEditingBillingInfo, setIsEditingBillinfInfo] = useState(false);
  const intl = useIntl();

  function isErrorFree(errorObj: any) {
    return Object.values(errorObj).every((val) => val === "");
  }

const validateCustomerInfo = () => {
  let hasError = false;

  if (fieldVisibility.isCompanyName && formData.selectedBusiness === 'Business to Business') {
    validateCompanyName(formData.companyName, dispatch);
    hasError ||= !formData.companyName;
  } else {
    dispatch({ type: 'SET_CUSTOMER_ERROR', field: 'companyNameError', value: '' });
  }

  setIsEditingCustomerInfo(
    formData.selectedBusiness === 'Business to Business'
      ? hasError || !isErrorFree(formErrorState.customerError)
      : false
  );
};

  const validateContactInfo = () => {
    if (isErrorFree(formErrorState.contactError)) {
      setIsEditingContactInfo(false);
    }
  };

const validateBillingInfo = () => {
  const canSave = isErrorFree(formErrorState.billingError);

  if (formData.paymentMethod === "3390" && canSave) {
    setIsEditingBillinfInfo(false);
  } else {
    validateAccountHolder(formData.bankaccholder || "", dispatch);
    const validateIBAN =
      "paymentError" in formErrorState
        ? Object.values(formErrorState.paymentError).every((val) => val === "")
        : true;
    if (!validateIBAN) {
      return;
    }
    if (onEditingBillingInfoChange && canSave) {
      onEditingBillingInfoChange(false);
      setIsEditingBillinfInfo(false);
    }
  }
};


  return (
    <Form className="py-cust">
      {fieldVisibility.isLabelForCustomer && (
        <div className="customer-information" data-testid="payment_customer_info_section" >
          <Row className="flex-grow-1 mb-16">
            <h5 className="section-title" data-testid="pm-text" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
              <FormattedMessage
                id={CONSTANTS.PAYMENT_FORM.PAGE_HEADING}
                defaultMessage="Customer Information"
              />
              {!!isOverviewPage && <span className="text">
              {!isEditingCustomerInfo ? <a href="#" onClick={(e)=>{
                 e.preventDefault();
                setIsEditingCustomerInfo(true);
              }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
              <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/></a> : <a href="#" onClick={(e)=>{ 
                 e.preventDefault();
                validateCustomerInfo();
                
                }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
              <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/></a>
              }
              </span>}
            </h5>

            {!isOverviewPage && <span className="section-subtitle volte-medium" data-testid="pm-text-customer-info">
              <FormattedMessage
                id={CONSTANTS.PAYMENT_FORM.PAGE_SUB_HEADING}
                defaultMessage="Please enter your customer information"
              />
            </span>}
          </Row>
          <Row className="flex-grow-1">
            {fieldVisibility.isBussiness && (
              <Col xs={12} md={12} className="mb-24">
               {isOverviewPage && !isEditingCustomerInfo ? <div><label className="form-label" data-testid="pm-companyName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BUSINESS_RELATION, defaultMessage: "Business relation" })} <span style={{color:"#f50057"}}>*</span></label><div className='field-value-overview' >{
                    formData.selectedBusiness == "Business to Customer" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2C, defaultMessage: "Business to Customer" })
                      : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2B, defaultMessage: "Business to Business" })
                    }</div></div>
               :<DropdownField
                  disable={paymentMethodCode == 3390 ? false : true}
                  visible={fieldVisibility.isBussiness}
                  controlId="BussinesRelation"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.BUSINESS_RELATION, defaultMessage: "Business relation" })}
                  selectedItem={
                    formData.selectedBusiness == "Business to Customer" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2C, defaultMessage: "Business to Customer" })
                      : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2B, defaultMessage: "Business to Business" })
                    }
                  handleSelect={onSelect}
                  options={[
                    {
                      label: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2C, defaultMessage: "Business to Customer" })}`,
                      value: "Business to Customer",
                      testId: "pm-bussines-b2c",
                    },
                    {
                      label: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.BUSINESS_RELATION.B2B, defaultMessage: "Business to Business" })}`,
                      value: "Business to Business",
                      testId: "pm-bussines-b2b",
                    },
                  ]}
                  dataTestId="pm-bussines-relation-label"
                  required={paymentMethodCode == 3390 ? true : false}
                />}
              </Col>
            )}
            
            {fieldVisibility.isCompanyName && formData.selectedBusiness === 'Business to Business' && (
             <Col xs={12} md={12} className="mb-24">
                {isOverviewPage && !isEditingCustomerInfo  ? <div><label className="form-label" data-testid="pm-companyName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COMPANY_NAME, defaultMessage: "Company name" })} <span style={{color:"#f50057"}}>*</span></label><div className='field-value-overview' >{formData.companyName}</div></div>:
                <FormField
                  visible={fieldVisibility.isCompanyName}
                  controlId="companyname"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COMPANY_NAME, defaultMessage: "Company name" })}
                  value={formData.companyName}
                  onChange={(e) => onChange("companyName", e.target.value)}
                  validationFn={validateCompanyName}
                  dispatch={dispatch}
                  fromType='SET_CUSTOMER_ERROR'
                  fromField= 'companyNameError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.COMPANY_NAME, defaultMessage: "Enter company name" })}
                  dataTestId="pm-companyName-label"
                  errorStatus={formData.selectedBusiness === 'Business to Business' ? formErrorState.customerError.companyNameError == '' ? false : true : false}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='companyName'
                />}
                {formData.selectedBusiness === 'Business to Business' 
                  && <FormFieldError id='companyname-error' value={formErrorState.customerError.companyNameError}  userState={userInputState.companyName}testId='pm-companyname-error'/>}
              </Col>
            )}
          </Row>

          {(fieldVisibility.isFirstName || fieldVisibility.isLastName) && (
            <Row className="flex-grow-1">
              {fieldVisibility.isFirstName && (
                <Col xs={12} md={6} className="mb-24">
                 {isOverviewPage && !isEditingCustomerInfo  ? <div><label className="form-label" data-testid="pm-firstName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.FIRST_NAME, defaultMessage: "First name" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.firstName}</span>
              </div>:<FormField
                    visible={fieldVisibility.isFirstName}
                    controlId="firstName"
                    label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.FIRST_NAME, defaultMessage: "First name" })}
                    value={formData.firstName}
                    onChange={(e) => onChange("firstName", e.target.value)}
                    validationFn={validateFirstName}
                    dispatch={dispatch}
                    fromType='SET_CUSTOMER_ERROR'
                    fromField= 'firstNameError'
                    placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.FIRST_NAME, defaultMessage: "Enter first name" })}
                    dataTestId="pm-firstName-label"
                    errorStatus={formErrorState.customerError.firstNameError == '' ? false : true}
                    userInputState={userInputState}
                    setUserInputState={setUserInputState}
                    inputKey='firstName'
                  />}
                  <FormFieldError id='firstname-error' value={formErrorState.customerError.firstNameError} userState={userInputState.firstName} testId='pm-firstname-error'/>
                </Col>
              )}
              
              {fieldVisibility.isLastName && (
                <Col xs={12} md={6} className="mb-24">
                  {isOverviewPage && !isEditingCustomerInfo  ? <div ><label className="form-label" data-testid="pm-lastName-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.LAST_NAME, defaultMessage: "Last name" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.lastName}</span>
              </div>:<FormField
                    visible={fieldVisibility.isLastName}
                    controlId="lastName"
                    label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.LAST_NAME, defaultMessage: "Last name" })}
                    value={formData.lastName}
                    onChange={(e) => onChange("lastName", e.target.value)}
                    validationFn={validateLastName}
                    dispatch={dispatch}
                    fromType='SET_CUSTOMER_ERROR'
                    fromField= 'lastNameError'
                    placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.LAST_NAME, defaultMessage: "Enter surname" })}
                    dataTestId="pm-lastName-label"
                    errorStatus={formErrorState.customerError.lastNameError == '' ? false : true}
                    userInputState={userInputState}
                    setUserInputState={setUserInputState}
                    inputKey='lastName'
                  />}
                  <FormFieldError id='lastname-error' value={formErrorState.customerError.lastNameError} userState={userInputState.lastName}  testId='pm-lastname-error'/>
                </Col>
              )}
            </Row>
          )}

          <Row className="flex-grow-1">
            {fieldVisibility.isDob && (
              <Col xs={12} md={12} className="mb-24">
                {isOverviewPage && !isEditingCustomerInfo  ? <div ><label className="form-label" data-testid="pm-dob-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DATE_OF_BIRTH, defaultMessage: "Date of birth" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.dob}</span>
              </div>:<FormField
                  visible={fieldVisibility.isDob}
                  controlId="dob"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DATE_OF_BIRTH, defaultMessage: "Date of birth" })}
                  value={formData.dob}
                  onChange={handleDateChange}
                  validationFn={validateDob}
                  dispatch={dispatch}
                  fromType='SET_CUSTOMER_ERROR'
                  fromField= 'dobError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.DATE_OF_BIRTH, defaultMessage: "DD.MM.YYYY" })}
                  dataTestId="pm-dob-label"
                  errorStatus={formErrorState.customerError.dobError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='dob'
                />}
                <FormFieldError id='dob-error' value={formErrorState.customerError.dobError} userState={userInputState.dob}  testId='pm-dob-error'/>
              </Col>
            )}
          </Row>
        </div>
      )}

      {fieldVisibility.isLabelForContact && (
        <div className="contact-information mt-2">
          <Row className="mb-16">
            <legend data-testid="pm-label-contact-info" className="section-title" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
              {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.CONTACT_INFORMATION, defaultMessage: "Contact Information" })}
              {!!isOverviewPage && <span className="text">
              {!isEditingContactInfo ? <a href="#" onClick={(e)=>{
                e.preventDefault();
                setIsEditingContactInfo(true)
              }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/>
              </a>:
              <a href="#" onClick={(e)=>{
                e.preventDefault();
                validateContactInfo();
              }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/>
              </a>
              }
              </span>}
            </legend>
           
            {!isOverviewPage && <span className="section-subtitle volte-medium" data-testid="pm-text">
              <FormattedMessage
                id={CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.CONTACT_SUB_INFORMATION}
                defaultMessage="Please fill in your contact information"
              />
            </span>}
          </Row>

          <Row>
            {fieldVisibility.isEmail && (
              <Col className="mb-24" xs={12} md={12}>
                {isOverviewPage && !isEditingContactInfo ? <div ><label className="form-label" data-testid="pm-email-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.EMAIL_ADDRESS, defaultMessage: "Email address" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.email}</span>
              </div>:<FormField
                  visible={fieldVisibility.isEmail}
                  controlId="email"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.EMAIL_ADDRESS, defaultMessage: "Email address" })}
                  value={formData.email}
                  onChange={(e) => onChange("email", e.target.value.replace(/\s/g, ''))}
                  validationFn={validateEmail}
                  dispatch={dispatch}
                  fromType='phoneError'
                  fromField= 'emailError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.EMAIL_ADDRESS, defaultMessage: "Enter email address" })}
                  type="email"
                  dataTestId="pm-email-label"
                  errorStatus={formErrorState.contactError.emailError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='email'
                />}
                <FormFieldError id='email-error' value={formErrorState.contactError.emailError} userState={userInputState.email} testId='pm-email-error'/>
              </Col>
            )}

            {fieldVisibility.isPhone && (
              <Col xs={12} md={12} className="mb-24">
                {isOverviewPage && !isEditingContactInfo ? <div ><label className="form-label" data-testid="pm-phone-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.PHONE_NUMBER, defaultMessage: "Phone number" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.phone}</span>
              </div>:<FormField
                  visible={fieldVisibility.isPhone}
                  controlId="phone"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.PHONE_NUMBER, defaultMessage: "Phone number" })}
                  value={formData.phone}
                  onChange={(e) => onChange("phone", e.target.value)}
                  validationFn={validatePhoneNumber}
                  dispatch={dispatch}
                  fromType='SET_CONTACT_ERROR'
                  fromField= 'phoneError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.PHONE_NUMBER, defaultMessage: "Enter phone number" })}
                  dataTestId="pm-phone-label"
                  errorStatus={formErrorState.contactError.phoneError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='phone'
                />}
                <FormFieldError id='phone-error' value={formErrorState.contactError.phoneError} userState={userInputState.phone} testId='pm-phone-error'/>
              </Col>
            )}
          </Row>
        </div>
      )}

      {fieldVisibility.isLabelForBilling && (
        <div className="billing-address mt-2">
          <Row className="mb-16">
            <legend data-testid="pm-label-billingaddress" className="section-title" style={{ display: isOverviewPage ? 'inline-flex' : 'block' }}>
              {intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.BILLING_ADDRESS, defaultMessage: "Billing address" })}
              {!!isOverviewPage && <span className="text">
              {!isEditingBillingInfo ? <a href="#" onClick={(e)=>{
                e.preventDefault(); 
                  setIsEditingBillinfInfo(true);
                    if (onEditingBillingInfoChange) {
                      onEditingBillingInfoChange(true);
                    }
              }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                <FormattedMessage id={CONSTANTS.FORM_EDIT.EDIT_LINK_TEXT} defaultMessage="Edit"/>
              </a>:
              <a href="#" onClick={(e)=>{
               e.preventDefault(); 
              validateBillingInfo();
              }} className="text-decoration-underline" style={{ fontSize: '1rem', marginLeft: 8 }}>
                <FormattedMessage id={CONSTANTS.FORM_EDIT.SAVE_BUTTON_TEXT} defaultMessage="Save"/>
              </a>
              }
              </span>}
            </legend>
            {!isOverviewPage && <span className="section-subtitle volte-medium" data-testid="pm-text">
              <FormattedMessage
                id={CONSTANTS.PAYMENT_FORM.SECTION_HEADINGS.BILLING_SUB_HEADING}
                defaultMessage="Please fill in your contact information"
              />
            </span>}
          </Row>

          <Row>
            {fieldVisibility.isStreet && (
              <Col xs={12} md={12} className="mb-24">
                {isOverviewPage && !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-streetaddress-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Street name and house number" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.address}</span>
              </div>:<FormField
                  visible={fieldVisibility.isStreet}
                  controlId="streetAddress"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Street name and house number" })}
                  value={formData.address}
                  onChange={(e) => onChange("address", e.target.value)}
                  validationFn={validateStreet}
                  dispatch={dispatch}
                  fromType='SET_BILLING_ERROR'
                  fromField= 'addressError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.STREET_NAME_AND_HOUSE_NUMBER, defaultMessage: "Enter street name and house number" })}
                  dataTestId="pm-streetaddress-label"
                  errorStatus={formErrorState.billingError.addressError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='address'
                />}
                <FormFieldError id='address-error' value={formErrorState.billingError.addressError} userState={userInputState.address} testId='pm-address-error'/>
              </Col>
            )}
          </Row>

          <Row>
            {fieldVisibility.isZip && (
              <Col xs={12} md={6} className="mb-24">
                {isOverviewPage && !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-zipcode-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.ZIP_CODE, defaultMessage: "Zip code" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.zipcode}</span>
              </div>:<FormField
                  visible={fieldVisibility.isZip}
                  controlId="zip-code"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.ZIP_CODE, defaultMessage: "Zip code" })}
                  value={formData.zipcode}
                  onChange={(e) => onChange("zipcode", e.target.value)}
                  validationFn={validateZip}
                  dispatch={dispatch}
                  fromType='SET_BILLING_ERROR'
                  fromField= 'zipcodeError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.ZIP_CODE, defaultMessage: "Enter zip code" })}
                  dataTestId="pm-zipcode-label"
                  errorStatus={formErrorState.billingError.zipcodeError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='zip'
                />}
                <FormFieldError id='zipcode-error' value={formErrorState.billingError.zipcodeError} userState={userInputState.zip} testId='pm-zipcode-error'/>
              </Col>
            )}
            
            {fieldVisibility.isCity && (
              <Col xs={12} md={6} className="mb-24">
                {isOverviewPage && !isEditingBillingInfo ? <div ><label className="form-label" data-testid="pm-city-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CITY, defaultMessage: "City" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.city}</span>
              </div>:<FormField
                  visible={fieldVisibility.isCity}
                  controlId="city"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.CITY, defaultMessage: "City" })}
                  value={formData.city}
                  onChange={(e) => onChange("city", e.target.value)}
                  validationFn={validateCity}
                  dispatch={dispatch}
                  fromType='SET_BILLING_ERROR'
                  fromField= 'cityError'
                  placeholder={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.PLACEHOLDER.CITY, defaultMessage: "Enter city name" })}
                  dataTestId="pm-city-label"
                  errorStatus={formErrorState.billingError.cityError == '' ? false : true}
                  userInputState={userInputState}
                  setUserInputState={setUserInputState}
                  inputKey='city'
                />}
                <FormFieldError id='city-error' value={formErrorState.billingError.cityError} userState={userInputState.city} testId='pm-city-error'/>
              </Col>
            )}
          </Row>

          <Row>  
            {fieldVisibility.isCountryCode && (
              <Col xs={12} md={12} className="mb-24">
                {isOverviewPage && !isEditingBillingInfo ? <div><label className="form-label" data-testid="pm-country-label">{intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COUNTRY, defaultMessage: "Country" })} <span style={{color:"#f50057"}}>*</span></label>
                <span className='field-value-overview' >{formData.selectedCountry}</span>
              </div>:<DropdownField
                  disable={false}
                  visible={true}
                  required={true}
                  controlId="Country"
                  label={intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.COUNTRY, defaultMessage: "Country" })}
                  selectedItem={
                    formData.selectedCountry == "Austria" ? intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.AUSTRIA, defaultMessage: "Austria" })
                      : intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "GERMANY" })
                    }
                  handleSelect={handleSelectCountry}
                  options={[
                    {
                      label: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.AUSTRIA, defaultMessage: "Austria" })}`,
                      value: "Austria",
                      testId: "pm-country-austria",
                    },
                    {
                      label: `${intl.formatMessage({ id: CONSTANTS.PAYMENT_FORM.LABEL.DROPDOWN_OPTIONS.COUNTRY.GERMANY, defaultMessage: "Germany" })}`,
                      value: "Germany",
                      testId: "pm-country-germany",
                    },
                  ]}
                  dataTestId="pm-country-label"
                />}
              </Col>
            )}
          </Row>
        </div>
      )}
    </Form>
  );
};

export default FormComponent;
